import { useState, useRef, useEffect } from "react";
import { loadProfile, clearProfile, formatProfileForPrompt } from "./memory/userProfile";
import { extractAndUpdateProfile } from "./memory/entityExtractor";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { runOrchestrator } from "./agents/orchestrator";
import "./App.css";

const USE_BACKEND = true;
const BACKEND_URL = 'https://api.rakaba.io';

// ── RAKABA FOUR-LEAF CLOVER LOGO ──────────────────────────────────────────
function RakabaLogo({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="13" cy="13" rx="7" ry="10" transform="rotate(-45 13 13)" fill="#10b981" opacity="0.95"/>
      <ellipse cx="23" cy="13" rx="7" ry="10" transform="rotate(45 23 13)" fill="#10b981" opacity="0.75"/>
      <ellipse cx="13" cy="23" rx="7" ry="10" transform="rotate(45 13 23)" fill="#10b981" opacity="0.75"/>
      <ellipse cx="23" cy="23" rx="7" ry="10" transform="rotate(-45 23 23)" fill="#10b981" opacity="0.55"/>
      <circle cx="18" cy="18" r="3.5" fill="#052e16"/>
      <line x1="18" y1="21.5" x2="18" y2="30" stroke="#10b981" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

function BotAvatar() {
  return (
    <svg width="18" height="18" viewBox="0 0 36 36" fill="none">
      <ellipse cx="13" cy="13" rx="7" ry="10" transform="rotate(-45 13 13)" fill="#10b981" opacity="0.95"/>
      <ellipse cx="23" cy="13" rx="7" ry="10" transform="rotate(45 23 13)" fill="#10b981" opacity="0.75"/>
      <ellipse cx="13" cy="23" rx="7" ry="10" transform="rotate(45 13 23)" fill="#10b981" opacity="0.75"/>
      <ellipse cx="23" cy="23" rx="7" ry="10" transform="rotate(-45 23 23)" fill="#10b981" opacity="0.55"/>
      <circle cx="18" cy="18" r="3.5" fill="#052e16"/>
    </svg>
  );
}

// ── TOOL DEFINITIONS ──────────────────────────────────────────────────────
const tools = [{
  functionDeclarations: [
    { name: "search_web", description: "Search the web for real-world facts, current events, people, places.", parameters: { type: "OBJECT", properties: { query: { type: "STRING", description: "The search query" } }, required: ["query"] } },
    { name: "calculate", description: "Evaluate a math expression. Use for any arithmetic or numeric calculations.", parameters: { type: "OBJECT", properties: { expression: { type: "STRING", description: "Math expression e.g. '250 * 0.18'" } }, required: ["expression"] } },
  ]
}];

async function search_web({ query }) {
  try {
    const res = await fetch(`https://api.duckduckgo.com/?q=${encodeURIComponent(query)}&format=json&no_html=1&skip_disambig=1`);
    const data = await res.json();
    const answer = data.AbstractText || data.Answer || data.RelatedTopics?.[0]?.Text;
    return { result: answer || `No result found. Please answer "${query}" using your knowledge.` };
  } catch { return { result: `Please answer "${query}" using your knowledge.` }; }
}

function calculate({ expression }) {
  const banned = ["process", "require", "import", "eval", "window"];
  if (banned.some(w => expression.includes(w))) return { result: "Invalid expression." };
  try {
    // eslint-disable-next-line no-new-func
    return { result: new Function(`"use strict"; return (${expression})`)().toString() };
  } catch { return { result: "Calculation error." }; }
}

const toolHandlers = { search_web, calculate };
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

const TOOL_LABELS = {
  search_web: "Searching the web...",
  calculate:  "Calculating...",
};

async function runAgent(userMessage, history, onToolCall, profile) {
  const profileContext = formatProfileForPrompt(profile);
  const model = genAI.getGenerativeModel({
    model: "gemini-2.5-flash",
    tools,
    systemInstruction: `You are Rakaba, an AI sales intelligence assistant. You help sales teams, founders, and growth professionals with research, prospecting, and outreach. Tools available: search_web, calculate. Be concise and sales-focused.${profileContext}`
  });
  const chat = model.startChat({ history });
  let response = await chat.sendMessage(userMessage);
  let candidate = response.response.candidates[0];
  let content = candidate.content;
  while (content.parts.some(p => p.functionCall)) {
    const { name, args } = content.parts.find(p => p.functionCall).functionCall;
    onToolCall(name);
    const toolResult = await toolHandlers[name](args);
    response = await chat.sendMessage([{ functionResponse: { name, response: toolResult } }]);
    candidate = response.response.candidates[0];
    content = candidate.content;
  }
  return response.response.text();
}

async function callBackendStream(userMessage, sessionId, onChunk, onTool, onDone) {
  const res = await fetch(`${BACKEND_URL}/api/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': process.env.REACT_APP_API_KEY },
    body: JSON.stringify({ message: userMessage, sessionId })
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const lines = decoder.decode(value).split('\n').filter(l => l.startsWith('data: '));
    for (const line of lines) {
      try {
        const data = JSON.parse(line.replace('data: ', ''));
        if (data.type === 'chunk') onChunk(data.text);
        if (data.type === 'tool')  onTool(data.tool);
        if (data.type === 'done')  onDone(data);
        if (data.type === 'error') throw new Error(data.message);
      } catch (e) { /* skip */ }
    }
  }
}

// ── SUGGESTED PROMPTS ─────────────────────────────────────────────────────
const SUGGESTED = [
  { icon: "🔍", text: "Search latest AI news",  prompt: "What are the latest developments in AI this week?" },
  { icon: "🧮", text: "Calculate deal ROI",      prompt: "If I close 3 deals at $8,000 each this month, what's my quarterly run rate?" },
  { icon: "📊", text: "Sales market research",   prompt: "What are the biggest pain points for B2B sales teams in 2025?" },
  { icon: "✉️", text: "Cold email frameworks",   prompt: "What makes a cold email subject line get opened? Give me 5 proven frameworks." },
];

export default function App() {
  const [messages, setMessages] = useState(() => {
    try { const s = localStorage.getItem('rakaba-messages'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [history, setHistory] = useState(() => {
    try { const s = localStorage.getItem('rakaba-history'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [input, setInput]                 = useState("");
  const [loading, setLoading]             = useState(false);
  const [toolStatus, setToolStatus]       = useState("");
  const [userProfile, setUserProfile]     = useState(() => loadProfile());
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming]     = useState(false);
  const [lastCost, setLastCost]           = useState(null);
  const [showSuggested, setShowSuggested] = useState(true);
  const bottomRef   = useRef(null);
  const textareaRef = useRef(null);

  const isEmpty = messages.length === 0;

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, toolStatus, streamingText]);

  useEffect(() => {
    if (messages.length > 0) setShowSuggested(false);
  }, [messages]);

  function clearChat() {
    setMessages([]); setHistory([]); setLastCost(null); setShowSuggested(true);
    localStorage.removeItem('rakaba-history');
    localStorage.removeItem('rakaba-messages');
  }

  function sendSuggested(prompt) {
    setInput(prompt);
    setTimeout(() => sendMessage(prompt), 0);
  }

  async function sendMessage(overrideText) {
    const userText = (overrideText || input).trim();
    if (!userText || loading) return;
    setInput('');
    setLoading(true);
    setStreamingText('');
    setToolStatus('');
    setShowSuggested(false);
    setMessages(prev => [...prev, { role: 'user', text: userText }]);

    try {
      const orchestratorAnswer = await runOrchestrator(userText, s => setToolStatus(s));
      if (orchestratorAnswer) {
        setMessages(prev => {
          const u = [...prev, { role: 'bot', text: orchestratorAnswer }];
          const t = u.slice(-50);
          localStorage.setItem('rakaba-messages', JSON.stringify(t));
          return t;
        });
        setHistory(prev => {
          const u = [...prev, { role: 'user', parts: [{ text: userText }] }, { role: 'model', parts: [{ text: orchestratorAnswer }] }];
          const t = u.slice(-20);
          localStorage.setItem('rakaba-history', JSON.stringify(t));
          return t;
        });
      } else if (USE_BACKEND) {
        setIsStreaming(true);
        let fullAnswer = '';
        await callBackendStream(
          userText,
          'react-ui',
          chunk => { fullAnswer += chunk; setStreamingText(fullAnswer); },
          tool  => setToolStatus(TOOL_LABELS[tool] || 'Processing...'),
          data  => {
            setLastCost(data.usage);
            setIsStreaming(false);
            setStreamingText('');
            setMessages(prev => {
              const u = [...prev, { role: 'bot', text: data.fullAnswer }];
              const t = u.slice(-50);
              localStorage.setItem('rakaba-messages', JSON.stringify(t));
              return t;
            });
            extractAndUpdateProfile(userText, data.fullAnswer).then(u => { if (u) setUserProfile(u); });
          }
        );
      } else {
        const answer = await runAgent(userText, history, tool => setToolStatus(TOOL_LABELS[tool] || 'Thinking...'), userProfile);
        setMessages(prev => {
          const u = [...prev, { role: 'bot', text: answer }];
          const t = u.slice(-50);
          localStorage.setItem('rakaba-messages', JSON.stringify(t));
          return t;
        });
      }
    } catch (err) {
      setIsStreaming(false);
      setStreamingText('');
      setMessages(prev => [...prev, {
        role: 'bot',
        text: err.message?.includes('429')
          ? 'Rate limit reached. Please wait a moment.'
          : 'Something went wrong. Please try again.'
      }]);
    }
    setToolStatus('');
    setLoading(false);
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  return (
    <div className="app">

      {/* ── HEADER ── */}
      <header className="header">
        <div className="header-left">
          <div className="header-logo"><RakabaLogo size={30}/></div>
          <div className="header-brand">
            <span className="brand-name">Raka<span>ba</span></span>
            <span className="brand-sep">/</span>
            <span className="brand-product">Sales Intelligence</span>
          </div>
        </div>
        <div className="header-center">
          <div className={`status-pill ${loading ? 'busy' : ''}`}>
            <span className={`status-dot ${loading ? 'busy' : ''}`}/>
            {loading ? 'Processing' : 'Online'}
          </div>
        </div>
        <div className="header-right">
          <a href="/leads" className="nav-link-btn primary-nav-btn">
            🎯 Research a Lead
          </a>
          <button className="clear-btn" onClick={clearChat} title="Clear conversation">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6M14 11v6"/>
            </svg>
            Clear
          </button>
        </div>
      </header>

      {/* ── PROFILE BAR ── */}
      {(userProfile.name || userProfile.job || userProfile.location) && (
        <div className="profile-bar">
          <span className="profile-label">Context</span>
          {userProfile.name     && <span className="profile-tag">{userProfile.name}</span>}
          {userProfile.job      && <span className="profile-tag">{userProfile.job}</span>}
          {userProfile.location && <span className="profile-tag">{userProfile.location}</span>}
          {userProfile.company  && <span className="profile-tag">{userProfile.company}</span>}
          <button className="profile-clear" onClick={() => { clearProfile(); setUserProfile(loadProfile()); }}>✕</button>
        </div>
      )}

      {/* ── COST BAR ── */}
      {lastCost && (
        <div className="cost-bar">
          <span className="cost-label">Last request</span>
          <span className="cost-tag">{lastCost.inputTokens} in</span>
          <span className="cost-tag">{lastCost.outputTokens} out</span>
          <span className="cost-tag cost-tag-green">{lastCost.estimatedCost}</span>
          <button className="profile-clear" style={{marginLeft:'auto'}} onClick={() => setLastCost(null)}>✕</button>
        </div>
      )}

      {/* ── MESSAGES AREA ── */}
      <div className="messages-wrap">

        {/* EMPTY STATE */}
        {isEmpty && (
          <div className="empty-state">
            <div className="empty-logo"><RakabaLogo size={52}/></div>
            <h1 className="empty-title">How can I help you today?</h1>
            <p className="empty-sub">
              Need to research a company before outreach? Try the{" "}
              <a href="/leads" style={{color:'#10b981', textDecoration:'none', fontWeight:500}}>
                Lead Research Pipeline
              </a>{" "}
              — research, score, and draft a cold email in under 60 seconds.
            </p>

            {showSuggested && (
              <div className="suggested-grid">
                {SUGGESTED.map((s, i) => (
                  <button key={i} className="suggested-card" onClick={() => sendSuggested(s.prompt)}>
                    <span className="suggested-icon">{s.icon}</span>
                    <span className="suggested-text">{s.text}</span>
                    <svg className="suggested-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                ))}
              </div>
            )}

            <div className="capability-pills">
              <span className="cap-pill"><span className="cap-dot"/>Web Search</span>
              <span className="cap-pill"><span className="cap-dot"/>Calculator</span>
              <span className="cap-pill"><span className="cap-dot"/>Sales Research</span>
              <span className="cap-pill"><span className="cap-dot"/>Memory</span>
            </div>
          </div>
        )}

        {/* MESSAGES */}
        <div className="messages">
          {messages.map((msg, i) => (
            <div key={i} className={`message ${msg.role}`}>
              {msg.role === 'bot' && (
                <div className="avatar bot-avatar"><BotAvatar/></div>
              )}
              <div className="bubble">
                {msg.role === 'bot'
                  ? <div dangerouslySetInnerHTML={{ __html: msg.text
                      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br/>')
                    }}/>
                  : msg.text
                }
              </div>
              {msg.role === 'user' && (
                <div className="avatar user-avatar">U</div>
              )}
            </div>
          ))}

          {/* Streaming */}
          {isStreaming && streamingText && (
            <div className="message bot">
              <div className="avatar bot-avatar"><BotAvatar/></div>
              <div className="bubble streaming">
                <div dangerouslySetInnerHTML={{ __html: streamingText
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n/g, '<br/>')
                }}/>
                <span className="cursor">▋</span>
              </div>
            </div>
          )}

          {/* Tool status */}
          {toolStatus && (
            <div className="message bot">
              <div className="avatar bot-avatar"><BotAvatar/></div>
              <div className="bubble tool-bubble">
                <div className="tool-spinner"/>
                <span>{toolStatus}</span>
              </div>
            </div>
          )}

          {/* Thinking dots */}
          {loading && !toolStatus && !isStreaming && (
            <div className="message bot">
              <div className="avatar bot-avatar"><BotAvatar/></div>
              <div className="bubble typing-bubble">
                <span className="dot"/><span className="dot"/><span className="dot"/>
              </div>
            </div>
          )}

          <div ref={bottomRef}/>
        </div>
      </div>

      {/* ── INPUT ── */}
      <div className="input-section">
        <div className="input-wrap">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask anything about sales, markets, or prospects..."
            disabled={loading}
            rows={1}
          />
          <button className="send-btn" onClick={() => sendMessage()} disabled={loading || !input.trim()}>
            {loading
              ? <div className="mini-spinner"/>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
            }
          </button>
        </div>
        <div className="input-footer">
          <span className="input-hint">Press Enter to send · Shift+Enter for new line</span>
          <span className="input-brand">Powered by <strong>Rakaba AI</strong></span>
        </div>
      </div>

    </div>
  );
}